import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { can as rbacCan } from "@/lib/rbac";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Layout from "@/components/app/Layout";
import Dashboard from "@/pages/Dashboard";
import Cylinders from "@/pages/Cylinders";
import Collections from "@/pages/Collections";
import Expenses from "@/pages/Expenses";
import Staff from "@/pages/Staff";
import DeliveryBoys from "@/pages/DeliveryBoys";
import Reports from "@/pages/Reports";
import BankDeposits from "@/pages/BankDeposits";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import DailyClosing from "@/pages/DailyClosing";
import Deliveries from "@/pages/Deliveries";
import DeliveryBoyProfile from "@/pages/DeliveryBoyProfile";
import ExpenseCategoriesPage from "@/pages/ExpenseCategoriesPage";
import AuditLogPage from "@/pages/AuditLogPage";
import BranchesPage from "@/pages/BranchesPage";
import DataResetPage from "@/pages/DataResetPage";
import InvoicesPage from "@/pages/InvoicesPage";
import BusinessProfilePage from "@/pages/BusinessProfilePage";

function Protected({ children, ownerOnly = false, needs = null }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400 overline" data-testid="loading-splash">
        Loading…
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (ownerOnly && user.role !== "owner") return <Navigate to="/" replace />;
  if (needs && !rbacCan(user, needs)) return <Navigate to="/" replace />;
  return children;
}

// Delivery-entry role only sees Deliveries + Expenses — land them there directly
// so they never see a blank / permission-denied Dashboard.
function LandingRedirect() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-slate-400 overline">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "delivery_entry") return <Navigate to="/deliveries" replace />;
  return <Dashboard />;
}

function AppShell() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/"
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route index element={<LandingRedirect />} />
        <Route path="cylinders" element={<Protected needs="cylinders:read"><Cylinders /></Protected>} />
        <Route path="delivery-boys" element={<Protected needs="boys:read"><DeliveryBoys /></Protected>} />
        <Route path="delivery-boys/:id" element={<Protected needs="boys:read"><DeliveryBoyProfile /></Protected>} />
        <Route path="customers" element={<Protected needs="customers:read"><Customers /></Protected>} />
        <Route path="customers/:id" element={<Protected needs="customers:read"><CustomerDetail /></Protected>} />
        <Route path="deliveries" element={<Protected needs="customers:read"><Deliveries /></Protected>} />
        <Route path="invoices" element={<Protected ownerOnly><InvoicesPage /></Protected>} />
        <Route path="settings/business" element={<Protected ownerOnly><BusinessProfilePage /></Protected>} />
        <Route path="collections" element={<Protected needs="collections:read"><Collections /></Protected>} />
        <Route path="expenses" element={<Protected needs="expenses:read"><Expenses /></Protected>} />
        <Route path="daily-closing" element={<Protected needs="closing:read"><DailyClosing /></Protected>} />
        <Route path="reports" element={<Protected needs="reports:read"><Reports /></Protected>} />
        <Route path="staff" element={<Protected ownerOnly><Staff /></Protected>} />
        <Route path="settings/expense-categories" element={<Protected ownerOnly><ExpenseCategoriesPage /></Protected>} />
        <Route path="settings/branches" element={<Protected ownerOnly><BranchesPage /></Protected>} />
        <Route path="settings/reset" element={<Protected ownerOnly><DataResetPage /></Protected>} />
        <Route path="audit-log" element={<Protected needs="audit:read"><AuditLogPage /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppShell />
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
